import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, LargePageDetails } from "@/types/technicalAudit";

interface LargePageCardProps {
	issue: PageIssue;
}

function formatBytes(bytes: number): string {
	if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
	return `${(bytes / 1024).toFixed(0)} KB`;
}

export function LargePageCard({ issue }: LargePageCardProps) {
	const details = issue.issue_details as LargePageDetails;
	const maxDisplay = Math.max(details.max_size_bytes * 2, details.page_size_bytes * 1.2);
	const percentage = Math.min((details.page_size_bytes / maxDisplay) * 100, 100);
	const thresholdPercent = (details.max_size_bytes / maxDisplay) * 100;
	const isHuge = details.page_size_bytes > details.max_size_bytes * 2;

	return (
		<div className="space-y-4">
			<div className={cn(
				"flex items-start gap-3 rounded-xl p-4 border",
				isHuge ? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800" : "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800",
			)}>
				<div className={cn(
					"flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
					isHuge ? "bg-red-100 dark:bg-red-900/40" : "bg-amber-100 dark:bg-amber-900/40",
				)}>
					<AlertTriangle className={cn("w-5 h-5", isHuge ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")} />
				</div>
				<div className="flex-1">
					<h4 className={cn("font-semibold", isHuge ? "text-red-900 dark:text-red-300" : "text-amber-900 dark:text-amber-300")}>
						Oversized Page
					</h4>
					<p className={cn("text-sm mt-1", isHuge ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400")}>
						This page is {formatBytes(details.page_size_bytes)}, exceeding the {formatBytes(details.max_size_bytes)} recommended maximum. Large pages increase load time and may hit browser memory limits.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Page Size
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className={cn("text-2xl font-bold", isHuge ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400")}>
							{formatBytes(details.page_size_bytes)}
						</span>
						<span className="text-sm text-muted-foreground">
							Max: {formatBytes(details.max_size_bytes)}
						</span>
					</div>
					<div className="relative">
						<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
							<div
								className={cn("h-full rounded-full transition-all", isHuge ? "bg-red-500" : "bg-amber-500")}
								style={{ width: `${percentage}%` }}
							/>
						</div>
						<div
							className="absolute top-0 w-0.5 h-5 -mt-1 bg-gray-600"
							style={{ left: `${thresholdPercent}%` }}
						/>
						<div
							className="absolute top-5 text-xs text-muted-foreground -translate-x-1/2"
							style={{ left: `${thresholdPercent}%` }}
						>
							threshold
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
